import { useAuth0 } from '@auth0/auth0-react';
import { Box } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';

import { GlobalReducerState } from 'app/reducers';
import {
  makeHasSelectedRecommendation,
  makeSelectCommercialField,
  makeSelectConfigField,
  makeSelectGlobalField,
} from 'Containers/App/selectors';
import { makeGetRecommendedHsaContribution } from 'Containers/CommercialRoutes/selectors';
import { makeSelectHsaField } from 'Containers/HsaPage/selectors';
import { makeSelectOverviewField } from 'Containers/OverviewPage/selectors';
import {
  makeGetBuilderGoSetting,
  makeGetSelectedProduct,
  makeGetSelectedPublicationPlanYear,
  makeGetSelectedPublicationSegmentSlug,
  makeSelectProfileField,
} from 'Containers/ProfilePage/selectors';
import { useFeatureFlagContext } from 'Contexts/featureFlagContext';
import { useProfileContext } from 'Contexts/profileContext';
import { FadeUp } from 'DesignLibrary/atoms';
import { PageLayout } from 'DesignLibrary/atoms/PageLayout';
import { Recommendation, SelectedSupplementalPlans } from 'Types/entities';
import { GetHsaRecommendationResponse } from 'Utils/apiTypes';
import {
  ERROR_PATH,
  getBCPath,
  getEnvironmentFromUrl,
  HSA_PLUS_PATH,
  INTRO_PATH,
  MTM_PATH,
  OVERVIEW_PATH,
  PROFILE_PATH,
  RESULT_PATH,
  TAX_SAVINGS_PATH,
  VIEW_PLAN_PATH,
  WELCOME_PATH,
} from 'Utils/urls';

import { Buttons } from './Buttons';
import Profile from './Profile';
import { Results } from './Results';
import { ButtonsWrapper, CtaContainer, CtaHalo, CtaPosition } from './styled';
import { DEPLOY_ENVIRONMENTS } from '../../../constants';
import { GO_GUEST_ID } from '../constants';
import { AlexProducts, JVPState } from '../types';
import { handleBcNavigate } from '../utils';

export interface CtaBarParentProps {
  isBuilderClientWithNoEligiblePlans: boolean;
  hasUnsavedProfileChanges: boolean;
}

export interface CtaBarStoreProps {
  selectedPlan: Recommendation | Record<string, never>;
  isCoverageWaived: boolean;
  selectedSupplementalPlans: SelectedSupplementalPlans;
  hsaData: GetHsaRecommendationResponse | Record<string, never>;
  fbsEnabled: boolean;
  hsaEnabled: boolean;
  taxSavingsEnabled: boolean;
  killMedRec: boolean;
  lockedHsaContribution: number | null;
  hsaContribution: number | null;
  recommendedHsaContribution: number | null;
  isIncentiveLoading: boolean;
  isEnrollmentLoading: boolean;
  hasSelectedRecommendation: boolean;
  isIntegratedUser: boolean;
  jvp: JVPState;
  isDemoMode: boolean;
  planYear: number | undefined;
  validatedSegmentSlug: string | undefined;
  bcUrlHash: string;
  selectedProduct: AlexProducts | '';
}

export type CtaBarProps = CtaBarParentProps & CtaBarStoreProps;

export const CtaBar = ({
  isBuilderClientWithNoEligiblePlans,
  selectedPlan,
  isCoverageWaived,
  selectedSupplementalPlans,
  fbsEnabled,
  hsaEnabled,
  taxSavingsEnabled,
  killMedRec,
  hsaData,
  lockedHsaContribution,
  hsaContribution,
  recommendedHsaContribution,
  isIncentiveLoading,
  isEnrollmentLoading,
  hasUnsavedProfileChanges,
  hasSelectedRecommendation,
  isIntegratedUser,
  jvp,
  isDemoMode,
  planYear,
  validatedSegmentSlug,
  bcUrlHash,
  selectedProduct,
}: CtaBarProps) => {
  const { getIsFormValid, getIsFormCompleted, shouldUpdateIncentives } = useProfileContext();
  const [isHsaUnlocked, setIsHsaUnlocked] = useState(false);
  const [isTaxSavingsUnlocked, setIsTaxSavingsUnlocked] = useState(false);
  const [isSupplementalUnlocked, setIsSupplementalUnlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { pathname } = useLocation();
  const path = pathname;
  const selectedProductIsBc = selectedProduct === 'bc';
  const { isAuthenticated, getIdTokenClaims } = useAuth0();

  // Logic to show Halo Glow effect
  const isFormValid = getIsFormValid();
  const isFormUnlocked = getIsFormCompleted();
  const showProfileGlow = path === PROFILE_PATH && isFormValid && isFormUnlocked;
  const showHealthGlow =
    (path === RESULT_PATH || path === VIEW_PLAN_PATH) && (hasSelectedRecommendation || isCoverageWaived);
  const showGlow = showProfileGlow || showHealthGlow;
  const { is_profile_review_tool_enabled } = useFeatureFlagContext();
  // Disable button while incentives call is being made
  useEffect(() => {
    if (shouldUpdateIncentives) {
      setIsLoading(true);
    }
  }, [shouldUpdateIncentives]);

  useEffect(() => {
    if (path === HSA_PLUS_PATH && !isHsaUnlocked) {
      setIsHsaUnlocked(true);
    }
    if (path === TAX_SAVINGS_PATH && !isTaxSavingsUnlocked) {
      setIsTaxSavingsUnlocked(true);
    }
    if (path === OVERVIEW_PATH && !isSupplementalUnlocked) {
      setIsSupplementalUnlocked(true);
    }
    if (!isLoading) {
      setIsLoading(true);
    }
  }, [path]);

  useEffect(() => {
    let timer: number;

    if (isLoading) {
      timer = window.setTimeout(() => setIsLoading(false), 1500);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [isLoading]);

  if (
    !path ||
    path === WELCOME_PATH ||
    path === MTM_PATH ||
    path === ERROR_PATH ||
    (path !== PROFILE_PATH && hasUnsavedProfileChanges)
  ) {
    return null;
  }

  const handleBcRedirect = () => {
    handleBcNavigate(
      jvp,
      isDemoMode,
      planYear,
      validatedSegmentSlug,
      bcUrlHash,
      GO_GUEST_ID,
      isAuthenticated,
      getIdTokenClaims,
      getBCPath,
    );
  };

  return (
    <CtaPosition>
      <FadeUp delay={4000} isOpen={path !== WELCOME_PATH && path !== INTRO_PATH}>
        <PageLayout bg="--primary-red" page="cta-bar">
          <Box pos="relative" zIndex={0}>
            <CtaContainer data-testid="cta-container" profile={path === PROFILE_PATH ? 1 : 0}>
              {path === PROFILE_PATH ? (
                <Profile />
              ) : (
                <Results
                  isHsaUnlocked={isHsaUnlocked}
                  isSupplementalUnlocked={isSupplementalUnlocked}
                  selectedPlan={selectedPlan}
                  isCoverageWaived={isCoverageWaived || isBuilderClientWithNoEligiblePlans}
                  selectedSupplementalPlans={selectedSupplementalPlans}
                  hsaContribution={hsaContribution}
                  recommendedHsaContribution={recommendedHsaContribution}
                  killMedRec={killMedRec}
                />
              )}
              <ButtonsWrapper>
                {is_profile_review_tool_enabled &&
                !isIntegratedUser &&
                path === PROFILE_PATH &&
                getEnvironmentFromUrl() !== DEPLOY_ENVIRONMENTS.PROD ? (
                  <Buttons
                    isLoading={isLoading || isIncentiveLoading || isEnrollmentLoading}
                    selectedPlan={selectedPlan}
                    isCoverageWaived={isCoverageWaived}
                    selectedSupplementalPlans={selectedSupplementalPlans}
                    fbsEnabled={fbsEnabled}
                    hsaEnabled={hsaEnabled}
                    taxSavingsEnabled={taxSavingsEnabled}
                    hsaData={hsaData}
                    lockedHsaContribution={lockedHsaContribution}
                    buttonText="profileReview"
                    dataTestId="cta-review-button"
                  />
                ) : null}

                <Buttons
                  isLoading={isLoading || isIncentiveLoading || isEnrollmentLoading}
                  selectedPlan={selectedPlan}
                  isCoverageWaived={isCoverageWaived}
                  selectedSupplementalPlans={selectedSupplementalPlans}
                  fbsEnabled={fbsEnabled}
                  hsaEnabled={hsaEnabled}
                  taxSavingsEnabled={taxSavingsEnabled}
                  hsaData={hsaData}
                  lockedHsaContribution={lockedHsaContribution}
                  handleBcRedirect={handleBcRedirect}
                  selectedProductIsBc={selectedProductIsBc}
                />
              </ButtonsWrapper>
            </CtaContainer>

            {showGlow && <CtaHalo />}
          </Box>
        </PageLayout>
      </FadeUp>
    </CtaPosition>
  );
};

const mapStateToProps = createStructuredSelector<GlobalReducerState, CtaBarStoreProps>({
  selectedPlan: makeSelectCommercialField('selectedHealthPlan'),
  isCoverageWaived: makeSelectCommercialField('isCoverageWaived'),
  selectedSupplementalPlans: makeSelectOverviewField('selected_plans'),
  recommendedHsaContribution: makeGetRecommendedHsaContribution(),
  lockedHsaContribution: makeSelectHsaField('lockedHsaContribution'),
  hsaData: makeSelectHsaField('hsaData'),
  fbsEnabled: makeSelectConfigField('is_fbs_enabled'),
  hsaEnabled: makeSelectConfigField('hsa_plus_enabled'),
  taxSavingsEnabled: makeSelectConfigField('show_tax_savings_page'),
  killMedRec: makeGetBuilderGoSetting('kill_med_rec'),
  hsaContribution: makeSelectHsaField('lockedHsaContribution'),
  isIncentiveLoading: makeSelectProfileField('incentiveSurveyIsLoading'),
  isEnrollmentLoading: makeSelectCommercialField('isEnrollmentLoading'),
  hasSelectedRecommendation: makeHasSelectedRecommendation(),
  isIntegratedUser: makeSelectConfigField('is_integrated'),
  jvp: makeSelectGlobalField('jvp'),
  isDemoMode: makeSelectGlobalField('isDemoMode'),
  planYear: makeGetSelectedPublicationPlanYear(),
  validatedSegmentSlug: makeGetSelectedPublicationSegmentSlug(),
  bcUrlHash: makeSelectGlobalField('bcUrlHash'),
  selectedProduct: makeGetSelectedProduct(),
});

const withConnect = connect(mapStateToProps);
export default compose(withConnect)(CtaBar);
